import { useCallback, useEffect, useMemo, useState } from 'react';
import { sayAlex, stopAlex } from '@/audio/alex';
import { navigate } from '@/app/router';
import { NAV, STEP_LINES, STEPS, doneLine, stepKey, type Step } from '@/content/agentBuilder';
import { CODENAMES, artFor, available, type LayerId, type Look } from '@/content/agentLook';
import { labBunny } from '@/content/helpers';
import { floors } from '@/content/floors';
import { builderMenu, builderOptions, paletteFor } from '@/content/menus';
import { commandCenter } from '@/content/scenes';
import { stripTags } from '@/content/story';
import { floorProgress } from '@/game/progress';
import { useProgress } from '@/game/progressStore';
import { agentStore, useAgent } from '@/game/agentProfile';
import { ageBand, useSettings } from '@/game/settings';
import { cx } from '@/lib/cx';
import type { Menu, MenuOption } from '@/speech/control/decide';
import { useVoiceMenu } from '@/speech/control/useVoiceMenu';
import { AgentFigure } from '@/ui/AgentFigure';
import { GameStage } from '@/ui/GameStage';
import { MicButton } from '@/ui/MicButton';
import { TopBar } from '@/ui/TopBar';
import styles from './AgentBuilder.module.css';

const backgrounds = {
  portrait: commandCenter.portrait.background,
  landscape: commandCenter.landscape.background,
};

/**
 * Option tiles show a close-up of the part a layer changes: the figure drawn at `height` CSS
 * pixels and shifted up by `top`, in a 92-pixel square. The head sits in the top fifth of the
 * canvas, the torso (suits, back gear) from about 24 to 62 % and the feet below 76 %.
 */
const HEAD = { height: 300, top: -6 };
const CLOSE_UPS: Partial<Record<LayerId, { height: number; top: number }>> = {
  hair: HEAD,
  eyewear: HEAD,
  headgear: HEAD,
  body: HEAD,
  face: HEAD,
  suit: { height: 242, top: -58 },
  back: { height: 242, top: -58 },
  shoes: { height: 330, top: -250 },
};

const palette = paletteFor;

/** A step is shown only when it has something to choose. */
function usable(step: Step, look: Look): boolean {
  if (step.kind === 'codename') return true;
  if (step.kind === 'colour') {
    const id = look.items[step.layer];
    return id !== undefined && id !== 'none' && artFor(step.layer, id) !== undefined;
  }
  return available(step.layer).some((i) => i.id !== 'none');
}

function shuffled<T>(items: readonly T[], random = Math.random): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [out[i], out[j]] = [out[j] as T, out[i] as T];
  }
  return out;
}

/** A whole random look (Surprise me): every layer that has art. */
function surprise(look: Look): Look {
  const items: Look['items'] = { ...look.items };
  const colours: Look['colours'] = { ...look.colours };
  for (const step of STEPS) {
    if (step.kind === 'item') {
      const choices = available(step.layer);
      const pick = choices[Math.floor(Math.random() * choices.length)];
      if (pick) items[step.layer] = pick.id;
    } else if (step.kind === 'colour') {
      const choices = palette(step.layer);
      const pick = choices[Math.floor(Math.random() * choices.length)];
      if (pick) colours[step.layer] = pick.id;
    }
  }
  return { ...look, items, colours };
}

/** The look with one option applied, for a tile's preview and for the pick itself. */
function withOption(look: Look, step: Step, id: string): Look {
  if (step.kind === 'item') return { ...look, items: { ...look.items, [step.layer]: id } };
  if (step.kind === 'colour') return { ...look, colours: { ...look.colours, [step.layer]: id } };
  return { ...look, codename: id };
}

/**
 * The agent builder (docs/design/agent-builder.md): the child makes their agent layer by layer,
 * by tapping a tile or saying it, with Agent Alex asking at each step; the codename comes last.
 */
export function AgentBuilder() {
  const stored = useAgent();
  const { age } = useSettings();
  const band = ageBand(age);
  const [look, setLook] = useState<Look>(() => ({
    items: { ...stored.items },
    colours: { ...stored.colours },
    codename: stored.codename,
  }));
  const steps = STEPS.filter((s) => usable(s, look));
  const [index, setIndex] = useState(0);
  const step = steps[Math.min(index, steps.length - 1)] ?? STEPS[0];
  const [names, setNames] = useState(() => shuffled(CODENAMES).slice(0, 4));
  const [finished, setFinished] = useState(false);
  /** Alex's reply on this step (a check-back, an answer); his question until there is one. */
  const [reply, setReply] = useState<{ step: string; text: string } | null>(null);

  const options = useMemo(
    () => (step ? builderOptions(step, names, available) : []),
    [step, names],
  );
  const question = step ? (STEP_LINES[band][stepKey(step)] ?? '') : '';
  const menu: Menu | null = useMemo(
    () => (step && !finished ? builderMenu(step, band, names, available) : null),
    [step, finished, band, names],
  );

  const key = step ? stepKey(step) : '';
  const say = useCallback(
    (text: string) => {
      setReply({ step: key, text });
      void sayAlex(text);
    },
    [key],
  );

  const finish = useCallback(
    (final: Look) => {
      agentStore.save(final);
      setFinished(true);
      if (final.codename) say(doneLine(band, final.codename));
    },
    [band, say],
  );

  const choose = useCallback(
    (id: string) => {
      if (!step) return;
      if (id === NAV.next.id) setIndex((i) => Math.min(i + 1, steps.length - 1));
      else if (id === NAV.back.id) setIndex((i) => Math.max(0, i - 1));
      else if (id === NAV.surprise.id) setLook((l) => surprise(l));
      else if (id === NAV.more.id) setNames(shuffled(CODENAMES).slice(0, 4));
      else {
        const next = withOption(look, step, id);
        setLook(next);
        if (step.kind === 'codename') finish(next);
      }
    },
    [step, steps.length, look, finish],
  );

  const voice = useVoiceMenu(menu, choose, say);

  // Alex asks each step's question when it comes up.
  useEffect(() => {
    if (!finished && question) void sayAlex(question);
  }, [key, finished, question]);
  const line = reply?.step === key || finished ? (reply?.text ?? '') : question;
  useEffect(
    () => () => {
      stopAlex();
    },
    [],
  );

  const micOn = voice.state.kind !== 'off' && voice.state.kind !== 'error';
  const progress = useProgress();
  const leave = () => {
    const first = floors[0];
    // Made right after the intro: on to the first mission. Otherwise back to the elevator.
    if (first && floorProgress(progress, first.id).completions === 0)
      navigate({ page: 'floor', floorId: first.id });
    else navigate({ page: 'elevator' });
  };

  return (
    <GameStage backgrounds={backgrounds}>
      <div className={styles.screen}>
        <TopBar onBack={leave} onSettings={leave}>
          <span className={styles.title}>{finished ? 'Agent file' : 'Make your agent'}</span>
        </TopBar>
        <div className={styles.body}>
          <div className={cx(styles.preview, finished && styles.card)}>
            <AgentFigure look={look} height={finished ? 360 : 420} className={styles.figure} />
            {finished && look.codename && (
              <div className={styles.cardText}>
                <span className={styles.cardLabel}>Agent</span>
                <strong className={styles.codename}>{look.codename}</strong>
              </div>
            )}
          </div>
          <div className={styles.panel}>
            <div className={styles.alex}>
              <img src={labBunny.portraits.clipboard.url} alt="" className={styles.alexArt} />
              <p className={styles.bubble} aria-live="polite">
                {stripTags(line)}
              </p>
            </div>
            {finished ? (
              <div className={styles.actions}>
                <button type="button" className={styles.primary} onClick={leave}>
                  Let’s go!
                </button>
                <button
                  type="button"
                  className={styles.secondary}
                  onClick={() => {
                    setFinished(false);
                    setIndex(0);
                  }}
                >
                  Change my agent
                </button>
              </div>
            ) : (
              step && (
                <>
                  <div
                    className={cx(
                      styles.tiles,
                      step.kind === 'colour' && styles.swatches,
                      step.kind === 'codename' && styles.names,
                    )}
                  >
                    {options.map((o) => (
                      <OptionTile
                        key={o.id}
                        step={step}
                        option={o}
                        look={look}
                        selected={isSelected(look, step, o.id)}
                        confirming={voice.confirming === o.id}
                        onChoose={choose}
                      />
                    ))}
                  </div>
                  <div className={styles.nav}>
                    <button
                      type="button"
                      className={styles.secondary}
                      disabled={index === 0}
                      onClick={() => {
                        choose(NAV.back.id);
                      }}
                    >
                      Back
                    </button>
                    {step.kind === 'codename' ? (
                      <button
                        type="button"
                        className={styles.secondary}
                        onClick={() => {
                          choose(NAV.more.id);
                        }}
                      >
                        Other names
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className={styles.secondary}
                          onClick={() => {
                            choose(NAV.surprise.id);
                          }}
                        >
                          Surprise me
                        </button>
                        <button
                          type="button"
                          className={styles.primary}
                          onClick={() => {
                            choose(NAV.next.id);
                          }}
                        >
                          Next
                        </button>
                      </>
                    )}
                  </div>
                </>
              )
            )}
            <div className={styles.micRow}>
              <MicButton
                state={
                  voice.state.kind === 'listening'
                    ? 'listening'
                    : voice.state.kind === 'loading' || voice.state.kind === 'thinking'
                      ? 'busy'
                      : 'idle'
                }
                label={micOn ? 'Stop listening' : 'Answer by voice'}
                size={84}
                pressed={micOn}
                onPress={() => {
                  if (micOn) void voice.stop();
                  else void voice.start();
                }}
              />
              <span className={styles.heard}>
                {voice.state.kind === 'loading'
                  ? `Getting my ears ready… ${String(voice.state.percent)}%`
                  : voice.heard
                    ? `I heard: “${voice.heard}”`
                    : micOn
                      ? 'Say what you want!'
                      : 'Tap to talk to Alex'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </GameStage>
  );
}

function isSelected(look: Look, step: Step, id: string): boolean {
  if (step.kind === 'item') return (look.items[step.layer] ?? 'none') === id;
  if (step.kind === 'colour') return look.colours[step.layer] === id;
  return look.codename === id;
}

function OptionTile({
  step,
  option,
  look,
  selected,
  confirming,
  onChoose,
}: {
  step: Step;
  option: MenuOption;
  look: Look;
  selected: boolean;
  confirming: boolean;
  onChoose: (id: string) => void;
}) {
  const choose = () => {
    onChoose(option.id);
  };
  if (step.kind === 'codename')
    return (
      <button
        type="button"
        className={cx(styles.tile, styles.nameTile, selected && styles.selected)}
        aria-pressed={selected}
        onClick={choose}
      >
        {option.label}
      </button>
    );
  if (step.kind === 'colour') {
    const hex = palette(step.layer).find((c) => c.id === option.id)?.hex ?? '#ccc';
    return (
      <button
        type="button"
        className={cx(
          styles.tile,
          styles.swatch,
          selected && styles.selected,
          confirming && styles.confirming,
        )}
        aria-pressed={selected}
        aria-label={option.label}
        onClick={choose}
      >
        <span className={styles.dot} style={{ background: hex }} />
        <span className={styles.tileLabel}>{option.label}</span>
      </button>
    );
  }
  const closeUp = CLOSE_UPS[step.layer];
  return (
    <button
      type="button"
      className={cx(styles.tile, selected && styles.selected, confirming && styles.confirming)}
      aria-pressed={selected}
      onClick={choose}
    >
      <span className={cx(styles.thumb, closeUp && styles.closeUp)}>
        <AgentFigure
          look={withOption(look, step, option.id)}
          height={closeUp?.height ?? 120}
          style={closeUp ? { marginTop: closeUp.top } : undefined}
        />
      </span>
      <span className={styles.tileLabel}>{option.label}</span>
    </button>
  );
}
